import { ComparePageTemplate, type CompareContent } from './ComparePageTemplate';

const content: CompareContent = {
  competitor: 'iSmartRecruit',
  competitorSegment: 'India-based ATS / Recruitment Software',
  tagline:
    'iSmartRecruit covers the basics of applicant tracking. GoGrabJob Business goes beyond tracking — AI interviews, explainable scoring, multilingual support, and workflow automation built specifically for modern Indian recruiting teams.',
  intro:
    "iSmartRecruit is a familiar name in the Indian ATS market, offering affordable applicant tracking and a reasonable feature set for the price. But it was built in an era before AI screening was practical, and its architecture shows the limitations of that design. GoGrabJob Business was built AI-first from day one: structured data, automated interviews, and explainable role-fit scoring are not features bolted on — they are foundational. For Indian recruitment teams ready to move beyond manual screening, GoGrabJob Business is the natural next platform.",

  tableGroups: [
    {
      groupName: 'General',
      rows: [
        { feature: 'Free trial / starter plan',        ours: 'yes',         theirs: 'yes'       },
        { feature: 'Transparent public pricing',       ours: 'yes',         theirs: 'partial'   },
        { feature: 'Starting price',                   ours: 'Free → ₹999', theirs: '₹2,000+/mo'},
        { feature: 'India / INR pricing',              ours: 'yes',         theirs: 'yes'       },
        { feature: 'Setup time',                       ours: '< 48 hours',  theirs: '1–2 weeks' },
        { feature: 'Modern React / SPA web app',       ours: 'yes',         theirs: 'partial'   },
        { feature: 'Mobile-responsive interface',      ours: 'yes',         theirs: 'yes'       },
      ],
    },
    {
      groupName: 'Core ATS',
      rows: [
        { feature: 'Applicant tracking pipeline',      ours: 'yes',  theirs: 'yes'     },
        { feature: 'Job posting & career page',        ours: 'yes',  theirs: 'yes'     },
        { feature: 'Resume parsing',                   ours: 'AI',   theirs: 'yes'     },
        { feature: 'Job board posting (Naukri, Indeed)', ours: 'yes', theirs: 'yes'    },
        { feature: 'Interview scheduling',             ours: 'yes',  theirs: 'yes'     },
        { feature: 'Offer letter generation',          ours: 'yes',  theirs: 'yes'     },
        { feature: 'Multi-user / team access',         ours: 'yes',  theirs: 'yes'     },
        { feature: 'Requisition approval workflow',    ours: 'yes',  theirs: 'partial' },
      ],
    },
    {
      groupName: 'AI & Assessment',
      rows: [
        { feature: 'AI video + voice interviews',       ours: 'yes',               theirs: 'no'     },
        { feature: 'Explainable role-fit scoring',      ours: 'Gap-level analysis', theirs: 'no'    },
        { feature: 'Automated candidate ranking',       ours: 'yes',               theirs: 'partial' },
        { feature: 'Multilingual AI: EN / HI / Hinglish', ours: 'yes',            theirs: 'no'     },
        { feature: 'Structured competency rubrics',     ours: 'yes',               theirs: 'partial' },
        { feature: 'Bias reduction controls',           ours: 'yes',               theirs: 'no'     },
      ],
    },
    {
      groupName: 'Automation & Workflow',
      rows: [
        { feature: 'Visual workflow automation engine',  ours: 'yes',  theirs: 'no'      },
        { feature: 'Email sequences & templates',        ours: 'yes',  theirs: 'partial' },
        { feature: 'Stage-change automation triggers',   ours: 'yes',  theirs: 'no'      },
        { feature: 'Calendar & self-scheduling',         ours: 'yes',  theirs: 'partial' },
        { feature: 'WhatsApp candidate communication',   ours: 'partial', theirs: 'yes'  },
      ],
    },
    {
      groupName: 'Agency & Multi-client',
      rows: [
        { feature: 'Agency client mode',                ours: 'yes',  theirs: 'partial' },
        { feature: 'Branded submission packs',          ours: 'yes',  theirs: 'partial' },
        { feature: 'Client-scoped pipeline access',     ours: 'yes',  theirs: 'partial' },
      ],
    },
    {
      groupName: 'Compliance & Security',
      rows: [
        { feature: 'GDPR tooling & DPA',         ours: 'yes',     theirs: 'partial' },
        { feature: 'DPDPA 2023 (India) aligned', ours: 'yes',     theirs: 'partial' },
        { feature: 'Full audit trail',           ours: 'yes',     theirs: 'yes'     },
        { feature: 'SSO (SAML / OIDC)',          ours: 'yes',     theirs: 'no'      },
        { feature: 'Data residency in India',    ours: 'yes',     theirs: 'yes'     },
        { feature: 'REST API access',            ours: 'yes',     theirs: 'partial' },
      ],
    },
  ],

  metrics: [
    { value: '5×',   label: 'Better AI assessment accuracy', sub: 'rubric-based structured scoring vs keyword matching' },
    { value: '3×',   label: 'Faster shortlisting',           sub: 'AI interviews vs manual phone screening' },
    { value: '80%',  label: 'Less screening time',           sub: 'per recruiter per week on high-volume roles' },
  ],

  deepDive: [
    {
      title: 'AI interviews: from aspirational to operational',
      body: "iSmartRecruit offers resume ranking and some automated filtering based on keywords — useful, but not the same as an AI interview. GoGrabJob Business conducts actual structured interviews: the AI asks role-specific questions, records responses, and scores each answer against a competency rubric. For a recruiter handling 80 applications for a Customer Success Manager role, the difference is between a keyword-sorted list of resumes (what iSmartRecruit provides) and a ranked shortlist of 8 candidates with scored assessments of communication skills, problem-solving, and customer empathy (what GoGrabJob Business provides).",
      highlight: "GoGrabJob AI interviews every applicant. iSmartRecruit filters resumes. These are not the same capability.",
    },
    {
      title: 'Multilingual AI built for how India actually communicates',
      body: "Indian professional communication rarely happens in pure English or pure Hindi. The code-switching blend of English and Hindi — Hinglish — is the working language of most Indian business conversations. GoGrabJob Business is the only AI interview platform that supports Hinglish as a first-class interview language, alongside English and Hindi. Candidates can respond naturally in the language they are most fluent in. iSmartRecruit's AI does not support multilingual screening at this level.",
    },
    {
      title: 'Visual workflow automation vs manual chasing',
      body: "iSmartRecruit's automation is limited to basic email notifications when a candidate status changes. GoGrabJob Business has a visual workflow engine where recruiters configure multi-step rules: 'When stage = AI Interview Complete AND score > 70 → send shortlist email to hiring manager AND assign calendar invite'. This eliminates the manual follow-up loops that consume recruiter time between application and offer. For agencies managing 20+ open roles simultaneously, the operational difference is significant.",
    },
    {
      title: 'Modern tech stack: speed and reliability that shows in daily use',
      body: "GoGrabJob Business was built on modern cloud infrastructure (AWS ap-south-1) using React, TypeScript, and a microservices backend. The application loads instantly, handles concurrent users without slowdowns, and receives continuous deployments. iSmartRecruit's architecture dates to an earlier generation — users commonly report page load issues, session timeouts, and interface inconsistencies that interrupt daily workflows. When a recruiter is managing a high-pressure hiring sprint, platform reliability is not a nice-to-have.",
    },
    {
      title: 'Explainable scoring that hiring managers can trust',
      body: "When iSmartRecruit surfaces a candidate ranking, the basis is primarily keyword matching and resume metadata. There is no structured assessment of how well the candidate can actually do the job. GoGrabJob Business produces a role-fit score with a breakdown: which competencies were demonstrated, which were absent, and the AI's evidence from the candidate's responses. Hiring managers see this alongside the resume — giving them a data-driven foundation for their interview shortlist decision instead of relying entirely on recruiter judgment.",
    },
  ],

  ourAdvantages: [
    'AI video + voice interviews for every applicant — fully automated',
    'Explainable gap-level role-fit scoring with evidence citations',
    'Multilingual AI supporting English, Hindi, and Hinglish',
    'Visual workflow automation engine — no-code stage-based rules',
    'GDPR + DPDPA 2023 compliant with full DPA and audit trail',
    'SSO (SAML / OIDC) for enterprise identity management',
    'Modern fast UI built on React/TypeScript — zero page-load friction',
    'Live in 48 hours with no implementation project required',
  ],

  theirAdvantages: [
    'WhatsApp native integration for candidate communication',
    'Longer market presence with larger Indian SME customer base',
    'Slightly lower starting price for basic resume-tracking needs',
    'Pre-built integrations with Indian job boards like Naukri, Shine',
    'Familiar UX for teams transitioning from spreadsheet-based tracking',
  ],

  faqs: [
    {
      q: "Can I import our existing candidates from iSmartRecruit?",
      a: "Yes. GoGrabJob Business accepts CSV imports for all standard candidate and job fields. iSmartRecruit supports CSV export from its database. We provide a field mapping guide for the migration and recommend a phased approach: import historical candidates first, then run both systems in parallel for active roles during a 2-week transition period before going fully live on GoGrabJob Business.",
    },
    {
      q: "Does GoGrabJob integrate with Naukri, LinkedIn, and Indian job boards?",
      a: "Yes. GoGrabJob Business integrates with Naukri, LinkedIn, Indeed, and Instahyre for job posting and direct application import. The Naukri and LinkedIn integrations support two-way sync for candidate status updates. Additional Indian job board integrations are on the 2026 roadmap.",
    },
    {
      q: "How does GoGrabJob handle WhatsApp communication with candidates?",
      a: "GoGrabJob Business currently sends interview invitations and status updates via email and SMS. Native WhatsApp integration is on our near-term roadmap (Q3 2026). In the interim, many customers use our webhook-based automation to trigger WhatsApp messages via third-party providers. Contact our team for details on available workarounds.",
    },
    {
      q: "We are a growing agency — which plan is right for us?",
      a: "Most growing agencies with 3–15 recruiters start on the Growth plan (₹1,999/seat/month or equivalent). This includes unlimited active roles, full AI interview capability, agency client mode, branded submission packs, and workflow automation. The Enterprise plan adds SSO, custom role-based access, dedicated onboarding, and SLA guarantees — typically adopted once a team exceeds 20 seats.",
    },
    {
      q: "Is GoGrabJob's AI compliant with India's Digital Personal Data Protection Act 2023?",
      a: "Yes. GoGrabJob Business is designed with DPDPA 2023 compliance as a foundational requirement. We act as a data processor for customer-controlled candidate data, with documented consent management, data minimisation defaults, and data subject request workflows. Our Data Protection contact and Grievance Officer details are published on our Privacy Policy page. We store all India-resident data in our Mumbai (AWS ap-south-1) data centre by default.",
    },
  ],
};

export function VsISmartRecruitPage() {
  return <ComparePageTemplate content={content} />;
}
